import { User } from '@src/entities/User/ui/components/User';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table: Tags
 *
 * Displays a list of tags within a DataTable.
 *
 * @param error
 * @param handleSave
 * @param headerLeft
 * @param isLoading
 * @param requestArgs
 * @param requestFunction
 * @param requestKey
 * @param rows
 * @param setCurrentEntity
 * @param setIsFormModalOpen
 * @param totalPages
 * @param totalRecords
 * @returns {Element}
 * @constructor
 */
export function Tags({
  error = null,
  handleSave = async () => {},
  headerLeft = null,
  isLoading = false,
  requestArgs = {},
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  setCurrentEntity = () => {},
  setIsFormModalOpen = () => {},
  totalPages = 1,
  totalRecords = 0,
}) {
  const { hasPermission } = useCurrentUser();

  const canEdit = hasPermission('Admin:Tags', 'edit');
  const canActivate = hasPermission('Admin:Tags', 'activate');
  const canDeactivate = hasPermission('Admin:Tags', 'deactivate');

  /**
   * getToggleStatus
   *
   * Retrieves the status of the toggle switch based on the user's permissions.
   *
   * @param statusValue
   * @returns {boolean}
   */
  const getToggleStatus = (statusValue) => {
    if (statusValue === true) {
      return canDeactivate;
    }

    if (statusValue === false) {
      return canActivate;
    }

    return false;
  };

  /**
   * renderCellActions
   *
   * Provides the user with a menu of actionable items to perform on a specific action path within the list of action
   * paths.
   *
   * @param row
   * @returns {Element}
   */
  const renderCellActions = ({ row }) => {
    const menuItems = [
      {
        disabled: !canEdit,
        icon: <IconSymbol icon="edit" />,
        onClick: () => {
          setIsFormModalOpen(true);
          setCurrentEntity(row?.original);
        },
        title: 'Edit',
      },
    ];

    return (
      <div
        style={{
          marginLeft: '-10px',
          marginRight: '-10px',
        }}
      >
        <Layout.Group
          css={{
            height: '100%',
            marginLeft: '2px',
            marginRight: '2px',
          }}
        >
          <DropdownMenu
            before={<IconSymbol color="#6F6F6F" icon="more_vert" size={24} />}
            hideLabel
            label=""
            menuItems={menuItems}
          />
        </Layout.Group>
      </div>
    );
  };

  /**
   * renderCellActive
   *
   * displays a toggle switch to enable or disable the tag.
   *
   * @param row
   * @returns {Element}
   */
  const renderCellActive = ({ row }) => {
    const canToggle = getToggleStatus(row?.original?.enabled);

    return (
      <User.Capability
        alias="toggle"
        attributes={['activate', 'deactivate']}
        isAllowed={canToggle}
        resource="Admin:Tags"
      >
        <ToggleSwitch
          isChecked={row?.original?.enabled}
          isDisabled={!row?.original?.userDefined}
          onChange={async () => {
            await handleSave({
              data: {
                categoryCode: row?.original?.categoryCode,
                code: row?.original?.code,
                enabled: row?.original?.enabled === false,
              },
              mode: 'edit',
            });
          }}
        />
      </User.Capability>
    );
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc']).map((item) => {
      const column = item;

      if (column.accessor === 'actions') {
        column.Cell = renderCellActions;
      }

      if (column.accessor === 'enabled') {
        column.Cell = renderCellActive;
      }

      return column;
    });
  }, [canEdit, canActivate, canDeactivate]);

  return (
    <ErrorHandler location="src/entities/Tag/ui/components/tables/Tags/Tags.jsx">
      <Styles>
        <TableComponent
          {...{
            columns,
            configuration,
            error,
            headerLeft,
            isLoading,
            noDataMessage: 'No tags found.',
            requestArgs,
            requestFunction,
            requestKey,
            rows,
            totalPages,
            totalRecords,
          }}
        />
      </Styles>
    </ErrorHandler>
  );
}
